'use strict';
const angular = require('angular');
const {copy, isFunction, isString, noop} = angular;

module.exports = [
  '$log',
  '$injector',
  '$compile',
  '$translate',
  DomainEditorPropertyDirective];

//TODO: move to AngularDomain project and setup configurator for template functions
//TODO: set up watches on the selector wherever needed to update properly

/**
 * API:
 *
 * {string} domain - the name of the domain class the editor is for
 * {string} property - the name of the property this editor is for
 *
 * {boolean} edit-mode - whether the elements starts in edit mode
 * {boolean} edit-mode-only - if true, directive does not support view mode
 *
 * {string} edit-class - the class to apply to the element when in edit mode
 * {string} view-class - the class to apply to the element when in view mode
 *
 * {Object} model - the domain instance to display/edit
 * {InPlaceEditor} editor - add this editor directive to the parent editor
 *
 * methods:
 *    {Function} isEditMode - returns whether this directive is currently in edit mode
 *    {Function} view - toggles the directive to view mode
 *    {Function} edit - toggles the directive to edit mode
 *    {Function} revert - reverts this editor's property to its original value, canceling the edit
 */

//TODO: add support for nested properties e.g. model.link.propertyName
function DomainEditorPropertyDirective($log, $injector, $compile, $translate) {
  var EDITOR_MODE = {
    VIEW: "view",
    EDIT: "edit"
  };

  return {
    restrict: 'E',
    template: '&nbsp;',
    priority: 600,
    scope: {
      model: '=?',
      editor: '=?'
    },
    controllerAs: 'propertyEditor',
    bindToController: true,
    controller: ['$scope', '$element', '$attrs', DomainEditorPropertyController],
    compile: function ($element, $attrs) {
      //$log.debug('DomainEditorPropertyDirective: compiling DomainEditorPropertyDirective');

      return {
        pre: function preLink($scope, $element, $attrs, controller) {
          //$log.debug('DomainEditorPropertyDirective: pre-linking DomainEditorPropertyDirective', controller);

          var properties;
          var editLinkFn;
          var viewLinkFn;

          var cancelCleanup = $scope.$on('$destroy', cleanup);
          var cancelInitObserve = $attrs.$observe('domain', function (newValue) {
            if (newValue) {
              properties = getDomainProperties(newValue, $attrs);
              editLinkFn = $compile(generateEditTemplate(properties.domain, properties.propertyName, properties.constraint, $attrs));
              viewLinkFn = $compile(generateViewTemplate(properties.domain, properties.propertyName, properties.constraint, $attrs));

              controller.$$editLinkFn = editLinkFn;
              controller.$$viewLinkFn = viewLinkFn;
              controller.init(properties);

              var mode = getInitialMode($attrs);

              if (mode === EDITOR_MODE.VIEW) {
                controller.setViewMode();
              } else {
                controller.setEditMode();
              }

              cancelInitObserve();
              cancelCleanup();
              cancelInitObserve = null;
            }
          });

          function cleanup() {
            isFunction(cancelInitObserve) && cancelInitObserve();
          }
        }
      }
    }
  };

  function DomainEditorPropertyController($scope, $element, $attrs) {
    var propertyName;
    var vm = this;

    vm.isEditMode = isEditMode.bind(vm);
    vm.init = init;
    vm.setViewMode = setViewMode.bind(vm, $scope, $element, $attrs);
    vm.setEditMode = setEditMode.bind(vm, $scope, $element, $attrs);
    vm.revert = revert;

    function init(properties) {
      $log.debug('DomainEditorPropertyDirective: initializing ' + properties.propertyName);
      propertyName = properties.propertyName;
      var editor = {};

      // Define API
      Object.defineProperties(editor, {
        view: {enumerable: true, value: vm.setViewMode},
        edit: {enumerable: true, value: vm.setEditMode},
        isEditMode: {enumerable: true, value: vm.isEditMode},
        revert: {enumerable: true, value: revert},
        setDomainList: {enumerable: true, configurable: true, value: setDomainList}
      });

      if (vm.editor) {
        vm.editor.addEditor(editor, propertyName);
      } else {
        console.warn('unable to add editor for property:' + propertyName);
      }

      if (properties.constraint.inList) {
        vm[properties.propertyName + 'List'] = copy(properties.constraint.inList);
      }

      var notDefined = noop();
      var nullValue = getPropertyNullValue(properties.domain.$name, propertyName);
      var type = properties.constraint.type;
      var trueVal, falseVal;
      // this may lead to blanks in some cases. the promise resolutions should trigger an new digest though.
      $translate('boolean.display.value.true').then(function (val) {
        trueVal = val;
      });
      $translate('boolean.display.value.false').then(function (val) {
        falseVal = val;
      });

      vm.getValue = function getValue() {
        if (vm.model) {
          var value = vm.model[propertyName];
          if (type === 'boolean') {
            return value ? trueVal : falseVal;
          } else if (value == null || value == notDefined) {
            return nullValue;
          } else {
            return value;
          }
        } else {
          return nullValue;
        }
      };

      function setDomainList(list) {
        $log.debug('DomainEditorPropertyDirective: setDomainList', propertyName + 'DomainList', list);
        vm[propertyName + 'DomainList'] = list;
      }

      $scope.$on('$destroy', cleanup);

      function cleanup() {
        if (vm.editor && vm.editor.removeEditor) {
          vm.editor.removeEditor(editor, propertyName);
        }
      }
    }

    function isEditMode() {
      return this.editMode;
    }

    function revert() {
      propertyName && vm.model && vm.model.$revert(propertyName);
    }
  }

  function setViewMode($scope, $element, $attrs) {
    this.editMode = false;
    $log.debug('DomainEditorPropertyDirective: switching to view mode');
    changeClass($element, $attrs, EDITOR_MODE.VIEW);

    updateTemplate.call(this, this.$$viewLinkFn, $scope, $element);
  }

  function setEditMode($scope, $element, $attrs) {
    this.editMode = true;
    $log.debug('DomainEditorPropertyDirective: switching to edit mode');
    changeClass($element, $attrs, EDITOR_MODE.EDIT);

    updateTemplate.call(this, this.$$editLinkFn, $scope, $element);
  }

  function updateTemplate(generator, $scope, $element) {
    $element.empty();
    generator($scope, function (clonedElement) {
      $element.append(clonedElement);
    });
  }

  function getDomainProperties(domainName, $attrs) {
    var properties = {
      domainName: domainName
    };

    try {
      properties.domain = $injector.get(properties.domainName);
    } catch (error) {
      throw new Error('DomainEditorPropertyDirective requires a valid domain attribute specifying the name of the Domain Class to use. Received: "' + domainName + '"');
    }

    if (typeof $attrs.property === 'undefined') {
      throw new Error('DomainEditorPropertyDirective requires a property attribute specifying the name of the property to edit.')
    } else {
      properties.propertyName = $attrs.property;
      properties.constraint = properties.domain.$constraints[properties.propertyName];
    }

    return properties;
  }

  function getPropertyLabel(domainName, property) {
    var label = [domainName, 'properties', property, 'label'].join('.');
    var result = $translate.instant(label);
    if (label == result) {
      result = property;
      console.warn('Missing label message for ' + label);
    }

    return result;
  }

  function getPropertyNullValue(domainName, property) {
    var label = [domainName, 'properties', property, 'null.message'].join('.');
    var result = $translate.instant(label);
    if (label == result) {
      result = null;
      console.warn('Missing null value message for ' + label);
    }

    return result;
  }

  function getInputType(type) {
    switch (type) {
      case 'email':
        return 'email';
        break;
      case 'number':
        return 'number';
        break;
      case 'Date':
        return 'date';
        break;
      case 'string':
        return 'text';
        break;
    }
  }

  function getInitialMode($attrs) {
    if (typeof $attrs.editMode === 'undefined' || $attrs.editMode === "false") {
      return EDITOR_MODE.VIEW
    } else {
      return EDITOR_MODE.EDIT
    }
  }

  function changeClass($element, $attrs, mode) {
    if (mode === EDITOR_MODE.VIEW) {
      if (isString($attrs.editClass)) {
        $element.removeClass($attrs.editClass)
      }

      if (isString($attrs.viewClass)) {
        $element.addClass($attrs.viewClass)
      }
    } else {
      if (isString($attrs.viewClass)) {
        $element.removeClass($attrs.viewClass)
      }

      if (isString($attrs.editClass)) {
        $element.addClass($attrs.editClass)
      }
    }
  }

  function generateViewTemplate(domain, propertyName, constraint) {
    //var nullValue = getPropertyNullValue(domain.$name, propertyName) || '&nbsp;';

    return '<span ng-bind="propertyEditor.getValue()"></span>';
  }

  function generateEditTemplate(domain, propertyName, constraint, $attrs) {
    var htmlBody = [];
    var inputType = getInputType(constraint.type);
    var propertyLabel = getPropertyLabel(domain.COLLECTION, propertyName);
    var nullValue = getPropertyNullValue(domain.COLLECTION, propertyName);
    var onBlur =  $attrs.onBlur ? ` ng-blur="${$attrs.onBlur}"` : null;
    var onChange =  $attrs.onChange? ` ng-change="${$attrs.onChange}"` : null;

    // start configure input
    switch (constraint.type) {
      case 'Date':
      case 'email':
      case 'number':
      case 'string':
        if (constraint.inList) {
          htmlBody.push(
            '<popover-select ng-model="propertyEditor.model[\'',
            propertyName,
            '\']" ng-options="listItem for listItem in propertyEditor.',
            propertyName, 'List"'
          );

          if (constraint.nullable) htmlBody.push(' nullable="\'', nullValue, '\'"');
          //if (constraint.nullable) htmlBody.push('<option value=""></option>');

          if (onBlur) htmlBody.push(onBlur);
          if (onChange) htmlBody.push(onChange);

          htmlBody.push('></popover-select>');
        } else {
          if (constraint.type === 'string' && constraint.widget === 'textarea') {
            htmlBody.push(
              '<textarea rows=4 ng-model="propertyEditor.model[\'', propertyName, '\']"',
              ' placeholder="', propertyLabel, '"'
            );

          } else {
            htmlBody.push(
              '<input type="', inputType, '" ng-model="propertyEditor.model[\'', propertyName, '\']"',
              ' placeholder="', propertyLabel, '"'
            );
          }

          if (!constraint.nullable) htmlBody.push(' required');

          if (constraint.minSize) htmlBody.push(' minlength="', constraint.minSize, '"');

          if (constraint.maxSize) htmlBody.push(' maxlength="', constraint.maxSize, '"');

          if (onBlur) htmlBody.push(onBlur);
          if (onChange) htmlBody.push(onChange);

          htmlBody.push('>');
          if (constraint.type === 'string') { // TODO: use length constraint to determine input vs textarea
            htmlBody.push('</textarea>');
          }
        }
        break;
      case 'boolean':
        //TODO(UI): Improve the in place editor's boolean appearance/UX
        htmlBody.push(
          //'<label class="checkbox">',
          '<input type="checkbox" ng-model="propertyEditor.model[\'', propertyName, '\']" aria-label="', propertyName, '">'//,
          //'</label>'
        );
        break;

      case 'Domain':
        //TODO: configure to allow null value (per constraints) once popover-select support is ready
        htmlBody.push(
          '<popover-select ng-model="propertyEditor.model[\'', propertyName, '\']"',
          ' options="item as item.toString() for item in propertyEditor.', propertyName + 'DomainList track by item.$id"'
        );

        if (constraint.nullable) htmlBody.push(' nullable="\'', nullValue, '\'"');

        htmlBody.push('></popover-select>');
        break;

      //TODO: implement collections and relations

      // TODO: objects will need to be handled explicitly
      case 'Object':
        //htmlBody.push(
        //  '<textarea ng-json ng-model="propertyEditor.model[\'', propertyName, '\']"></textarea>',
        //);
        break;
      // TODO: implement other types
      default:
        console.warn('Domain type "' + constraint.type + '" is not yet supported!');
    }

    //htmlBody.push(
    //  '<p ng-if="domain-propertyName-error="propertyEditor.model.$errors[\'',
    //  propertyName,
    //  '\'].length" domain-propertyName-error="propertyEditor.model.$errors[\'',
    //  propertyName,
    //  '\']" class="domain-propertyName-editor-error-tag"></p>'
    //); // end configure input

    return htmlBody.join('');
  }
}

function isBoolean(value) {
  return value === true || value === false;
}


// WEBPACK FOOTER //
// ./src/js/domain-directives/DomainEditorPropertyDirective.js